<template>
  <div class="tinto-page">
    <Loading :isActive="loading" />

    <TintometricoVincularEanModal
      name="coranteForm"
      context-title="Colorante em edição"
      :context-fields="coranteContextFields"
      :ean-atual="editForm.ean"
      :endpoint="`/admin/tintometrico/corante/${editForm.id}`"
      @saved="load"
    />

    <!-- ===== FILTERS ===== -->
    <div class="tinto-filters">
      <div class="tinto-filtros__linha">
        <div class="tinto-filtros__busca">
          <input class="form-control" v-model="filtros.termo" placeholder="Busca rápida: nome, código ou EAN..." @keyup.enter="load()" />
        </div>
        <div class="tinto-filtros__acoes">
          <button class="button button-primary button--icone" @click="load()" title="Buscar">
            <fa-icon icon="search" />
          </button>
          <button
            :class="['button', 'button--auto', filtrosAbertos ? 'button-primary' : '']"
            @click="filtrosAbertos = !filtrosAbertos"
          >
            <fa-icon icon="filter" /> Filtros
            <span v-if="totalFiltrosAtivos" class="tinto-filtros__contador">{{ totalFiltrosAtivos }}</span>
          </button>
        </div>
      </div>

      <div class="tinto-filtros__painel" v-if="filtrosAbertos">
        <CRow>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Fabricante</label>
              <select class="form-control" v-model="filtros.fabricanteId" @change="onFabricanteChange()">
                <option value="">Todos</option>
                <option v-for="(n,id) in fabricantes" :key="id" :value="id">{{ n }}</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Origem</label>
              <select class="form-control" v-model="filtros.integracaoId" @change="load()">
                <option value="">Todas</option>
                <option v-for="i in integracoesDoFabricante" :key="i.id" :value="i.id">{{ i.descricao }}</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Vínculo com produto</label>
              <select class="form-control" v-model="filtros.eanPreenchido" @change="load()">
                <option value="">Todos</option>
                <option value="1">Com produto</option>
                <option value="0">Sem produto</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Nome</label>
              <input class="form-control" v-model="filtros.nome" placeholder="contém..." @keyup.enter="load()" />
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Código externo</label>
              <input class="form-control" v-model="filtros.idExterno" placeholder="contém..." @keyup.enter="load()" />
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">EAN</label>
              <input class="form-control" v-model="filtros.ean" placeholder="contém..." @keyup.enter="load()" />
            </div>
          </CCol>
        </CRow>
      </div>

      <div class="tinto-chips" v-if="chipsFiltros.length">
        <span class="tinto-chips__titulo">Filtrando por</span>
        <span class="tinto-filtro-chip" v-for="chip in chipsFiltros" :key="chip.chave">
          <strong>{{ chip.rotulo }}:</strong> {{ chip.valor }}
          <span class="tinto-filtro-chip__remover" @click="removerFiltro(chip.chave)" title="Remover filtro">&times;</span>
        </span>
        <button class="tinto-chips__limpar" @click="limparFiltros()">limpar tudo</button>
      </div>
    </div>

    <!-- ===== TABLE ===== -->
    <div class="tinto-table-wrap">
      <table class="tinto-table">
        <thead><tr>
          <th style="width:50px">ID</th>
          <th style="width:280px" class="tinto-sortable" :class="{'is-active': sort.by==='nome'}" @click="sortBy('nome')">
            Nome<fa-icon :icon="sort.by==='nome' ? (sort.dir==='asc' ? 'arrow-up' : 'arrow-down') : 'arrow-down'" class="sort-icon" />
          </th>
          <th style="width:140px" class="tinto-sortable" :class="{'is-active': sort.by==='id_externo'}" @click="sortBy('id_externo')">
            Código Externo<fa-icon :icon="sort.by==='id_externo' ? (sort.dir==='asc' ? 'arrow-up' : 'arrow-down') : 'arrow-down'" class="sort-icon" />
          </th>
          <th style="width:110px;text-align:right" class="tinto-sortable" :class="{'is-active': sort.by==='volume_ml'}" @click="sortBy('volume_ml')">
            Volume (ml)<fa-icon :icon="sort.by==='volume_ml' ? (sort.dir==='asc' ? 'arrow-up' : 'arrow-down') : 'arrow-down'" class="sort-icon" />
          </th>
          <th style="width:130px">EAN</th>
          <th style="width:90px" title="Código do produto no ERP vinculado pelo EAN">Produto</th>
          <th style="width:110px">Fabricante</th>
          <th style="width:130px">Origem</th>
          <th style="width:50px"></th>
        </tr></thead>
        <tbody>
          <tr v-if="items.length === 0"><td colspan="9" class="tinto-table__empty">Nenhum colorante encontrado</td></tr>
          <tr v-for="r in items" :key="r.id">
            <td>{{ r.id }}</td>
            <td class="tinto-table__primary">{{ r.nome }}</td>
            <td>{{ r.idExterno }}</td>
            <td style="text-align:right">{{ r.volumeMl }}</td>
            <td>
              <span v-if="r.ean && !r.produtoId" class="tinto-ean--orfao" title="EAN sem produto correspondente no ERP — o colorante não soma no preço">
                {{ r.ean }} <fa-icon icon="exclamation-triangle" aria-hidden="true" />
                <span class="sr-only">EAN sem produto correspondente</span>
              </span>
              <span v-else-if="r.ean">{{ r.ean }}</span>
              <span v-else class="tinto-table__muted">—</span>
            </td>
            <td>
              <span v-if="r.produtoId">{{ r.produtoId }}</span>
              <span v-else class="tinto-table__muted">—</span>
            </td>
            <td>{{ r.fabricanteNome }}</td>
            <td>
              <span v-if="r.integracaoNome">{{ r.integracaoNome }}</span>
              <span v-else class="tinto-table__muted">—</span>
            </td>
            <td>
              <button class="button button-primary" @click="openEdit(r)" title="Gerenciar EAN">
                <fa-icon icon="pencil-alt" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="tinto-pagination" v-if="paginator.last_page > 1">
      <pagination :data="paginator" :limit="5" @pagination-change-page="load" />
    </div>
  </div>
</template>

<script>
import Loading from "../../components/Loading";
import TintometricoVincularEanModal from "./TintometricoVincularEanModal.vue";
import listagemMixin from "@/mixins/listagemMixin";

export default {
  name: "tintometrico-corante",
  components: { Loading, TintometricoVincularEanModal },
  mixins: [listagemMixin],
  props: ['fabricantes', 'integracoes'],

  data() {
    return {
      listagemEndpoint: '/admin/tintometrico/corante',
      sort: { by: 'nome', dir: 'asc' },
      filtros: { termo: '', fabricanteId: '', integracaoId: '', nome: '', idExterno: '', ean: '', eanPreenchido: '' },
      rotulosFiltros: {
        termo: 'Busca', fabricanteId: 'Fabricante', integracaoId: 'Origem', eanPreenchido: 'Vínculo',
        nome: 'Nome', idExterno: 'Código externo', ean: 'EAN',
      },
      editForm: { id: null, nome: '', idExterno: '', volumeMl: null, fabricanteNome: '', ean: '' },
    };
  },

  computed: {
    // Origens disponíveis para escolher: quando um fabricante está selecionado,
    // só as integrações dele. O catálogo tem uma cópia por integração quando o
    // fabricante tem mais de uma fonte (duas máquinas, por exemplo).
    integracoesDoFabricante() {
      if (!this.filtros.fabricanteId) return this.integracoes;
      return this.integracoes.filter((i) => String(i.fabricanteId) === String(this.filtros.fabricanteId));
    },

    volumeFormatado() {
      const v = this.editForm.volumeMl;
      if (!v) return '';
      return v >= 1000 ? (v / 1000).toFixed(1) + 'L' : v + 'ml';
    },

    coranteContextFields() {
      return [
        { label: 'ID',          value: this.editForm.id ? '#' + this.editForm.id : '—', badge: 'secondary', size: 'col-md-2' },
        { label: 'Código',      value: this.editForm.idExterno || '—',        badge: 'info',      size: 'col-md-4' },
        { label: 'Nome',        value: this.editForm.nome || '—',                                 size: 'col-md-6' },
        { label: 'Volume',      value: this.volumeFormatado || '—',                               size: 'col-md-6' },
        { label: 'Fabricante',  value: this.editForm.fabricanteNome || '—',                       size: 'col-md-6' },
      ];
    },
  },

  methods: {
    descreverFiltro(chave, valor) {
      if (chave === 'fabricanteId') return this.fabricantes[valor] || valor;
      if (chave === 'integracaoId') return this.nomeDaOpcao(this.integracoes, valor);
      if (chave === 'eanPreenchido') return valor === '1' ? 'com produto' : 'sem produto';
      return valor;
    },

    // Cada filtro tem o seu parâmetro e todos valem juntos (AND no backend).
    appendFiltros(params) {
      if (this.filtros.fabricanteId) params.append('fabricante_id', this.filtros.fabricanteId);
      if (this.filtros.termo) params.append('q', this.filtros.termo);
      if (this.filtros.nome) params.append('nome', this.filtros.nome);
      if (this.filtros.idExterno) params.append('id_externo', this.filtros.idExterno);
      if (this.filtros.ean) params.append('ean', this.filtros.ean);
      if (this.filtros.eanPreenchido !== '') params.append('ean_preenchido', this.filtros.eanPreenchido);
    },

    openEdit(corante) {
      this.editForm = {
        id: corante.id,
        nome: corante.nome,
        idExterno: corante.idExterno,
        volumeMl: corante.volumeMl,
        fabricanteNome: corante.fabricanteNome,
        ean: corante.ean,
      };
      this.$modal.show('coranteForm');
    },
  },
};
</script>
